import { gql } from '../../__generated__'

// Generic databaseListConfigs (not the custom publishedDatabaseListConfigs
// resolver) so the offline catalog replica can serve it — filtering/sorting run
// through the generated CATALOG_OP handler, which needs the where/orderBy as
// variables. Online it falls through to the same Keystone list query.
export const GET_PUBLISHED_DATABASE_LIST_CONFIGS = gql(/* GraphQL */ `
  query GetPublishedDatabaseListConfigs(
    $where: DatabaseListConfigWhereInput!
    $orderBy: [DatabaseListConfigOrderByInput!]!
  ) {
    databaseListConfigs(where: $where, orderBy: $orderBy) {
      id
      urlName
      nameDe
      nameEn
      subtitleDe
      subtitleEn
      mode
      listType
      filter
      sort
      ignoredFilterPropsForChangeDetection
      hideSearch
      order
    }
  }
`)

export const GET_MY_CUSTOM_LISTS = gql(/* GraphQL */ `
  query GetMyCustomLists {
    userCustomLists(orderBy: [{ order: asc }]) {
      id
      urlName
      name
      description
      targetType
      mode
      isPublic
      isCustomTag
      customTagName
      customIcon
      removeWhenCollected
      removeWhenRead
      showTotalNumber
      showTotalValue
      statusFilter
      filter
      sort
      order
    }
  }
`)

export const USER_CUSTOM_LIST_STATS = gql(/* GraphQL */ `
  query UserCustomListStats($listId: ID!) {
    userCustomListStats(listId: $listId) {
      itemCount
      seriesCount
      volumeCount
      totalValue
    }
  }
`)

export const GET_USER_CUSTOM_LIST = gql(/* GraphQL */ `
  query GetUserCustomList($id: ID!) {
    userCustomList(where: { id: $id }) {
      id
      urlName
      name
      description
      targetType
      mode
      isPublic
      isCustomTag
      customTagName
      customIcon
      removeWhenCollected
      removeWhenRead
      showTotalNumber
      showTotalValue
      statusFilter
      filter
      sort
      order
      curatedReleaseEntriesCount
      curatedNoveltiesCount
    }
  }
`)

export const GET_DATABASE_LIST_CURATED_ENTRIES = gql(/* GraphQL */ `
  query GetDatabaseListCuratedEntries(
    $where: DatabaseListReleaseEntryWhereInput!
    $orderBy: [DatabaseListReleaseEntryOrderByInput!]!
    $take: Int
    $skip: Int!
  ) {
    databaseListReleaseEntries(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      orderNumber
      releaseEntry {
        cover
        thumbHash
        id
        status
        internalName
        type
        publisherName
        startDate
        sortedVolumeNumbers
        notReleasedVolumeNumbers
      }
    }
  }
`)

export const GET_USER_LIST_CURATED_ENTRIES = gql(/* GraphQL */ `
  query GetUserListCuratedEntries(
    $where: UserCustomListReleaseEntryItemWhereInput!
    $orderBy: [UserCustomListReleaseEntryItemOrderByInput!]!
    $take: Int
    $skip: Int!
  ) {
    userCustomListReleaseEntryItems(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      orderNumber
      releaseEntry {
        cover
        thumbHash
        id
        status
        internalName
        type
        publisherName
        startDate
        sortedVolumeNumbers
        notReleasedVolumeNumbers
      }
    }
  }
`)

export const GET_USER_LIST_CURATED_NOVELTIES = gql(/* GraphQL */ `
  query GetUserListCuratedNovelties(
    $where: UserCustomListNoveltyItemWhereInput!
    $orderBy: [UserCustomListNoveltyItemOrderByInput!]!
    $take: Int
    $skip: Int!
  ) {
    userCustomListNoveltyItems(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      orderNumber
      novelty {
        id
        number
        volumeSuffix
        isbn
        romanizedSeriesTitle
        price
        publisherName
        localizedSeriesTitle
        cover
        thumbHash
        releaseDate
        releaseDateAccuracy
        entryId
        relationType
        relationId
        releaseItems
      }
    }
  }
`)

export const GET_FILTERED_CUSTOM_LIST_NOVELTIES = gql(/* GraphQL */ `
  query GetFilteredCustomListNovelties(
    $listId: ID!
    $where: NoveltyWhereInput!
    $orderBy: [NoveltyOrderByInput!]!
    $take: Int
    $skip: Int!
    $filterMode: String
  ) {
    filteredCustomListNovelties(
      listId: $listId
      where: $where
      orderBy: $orderBy
      take: $take
      skip: $skip
      filterMode: $filterMode
    ) {
      id
      number
      volumeSuffix
      isbn
      romanizedSeriesTitle
      price
      publisherName
      localizedSeriesTitle
      cover
      thumbHash
      releaseDate
      releaseDateAccuracy
      entryId
      relationType
      relationId
      releaseItems
      collectionVolumeInfo {
        volumeCount
        spansEntries
      }
    }
  }
`)

export const GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES = gql(/* GraphQL */ `
  query GetFilteredCustomListReleaseEntries(
    $listId: ID!
    $where: ReleaseEntryWhereInput!
    $orderBy: [ReleaseEntryOrderByInput!]!
    $take: Int
    $skip: Int!
    $filterMode: String
  ) {
    filteredReleaseEntries(
      listId: $listId
      where: $where
      orderBy: $orderBy
      take: $take
      skip: $skip
      filterMode: $filterMode
    ) {
      cover
      thumbHash
      id
      status
      internalName
      type
      publisherName
      startDate
      sortedVolumeNumbers
      notReleasedVolumeNumbers
    }
  }
`)

export const CREATE_USER_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation CreateUserCustomList($data: UserCustomListCreateInput!) {
    createUserCustomList(data: $data) {
      id
      urlName
    }
  }
`)

export const UPDATE_USER_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation UpdateUserCustomList($id: ID!, $data: UserCustomListUpdateInput!) {
    updateUserCustomList(where: { id: $id }, data: $data) {
      id
    }
  }
`)

export const DELETE_USER_CUSTOM_LIST_WITH_ITEMS = gql(/* GraphQL */ `
  mutation DeleteUserCustomListWithItems($listId: ID!) {
    deleteUserCustomListWithItems(listId: $listId) {
      id
    }
  }
`)

export const GET_MY_CURATED_LISTS_WITH_MEMBERS = gql(/* GraphQL */ `
  query GetMyCuratedListsWithMembers {
    userCustomLists(where: { mode: { equals: curated } }, orderBy: [{ order: asc }]) {
      id
      name
      targetType
      isCustomTag
      customTagName
      removeWhenCollected
      removeWhenRead
      curatedReleaseEntries {
        id
        releaseEntry {
          id
        }
      }
      curatedNovelties {
        id
        novelty {
          id
          entryId
          relationType
          relationId
          number
          volumeSuffix
          releaseItems
          collectionVolumeInfo {
            kind
          }
        }
      }
    }
  }
`)

export const GET_NOVELTY_ID_BY_RELATION = gql(/* GraphQL */ `
  query GetNoveltyIdByRelation($relationId: String!) {
    novelties(where: { relationId: { equals: $relationId } }, take: 1) {
      id
    }
  }
`)

export const ADD_RELEASE_ENTRY_TO_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation AddReleaseEntryToCustomList($listId: ID!, $releaseEntryId: ID!) {
    createUserCustomListReleaseEntryItem(
      data: { list: { connect: { id: $listId } }, releaseEntry: { connect: { id: $releaseEntryId } } }
    ) {
      id
    }
  }
`)

export const REMOVE_RELEASE_ENTRY_FROM_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation RemoveReleaseEntryFromCustomList($id: ID!) {
    deleteUserCustomListReleaseEntryItem(where: { id: $id }) {
      id
    }
  }
`)

export const ADD_NOVELTY_TO_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation AddNoveltyToCustomList($listId: ID!, $noveltyId: ID!) {
    createUserCustomListNoveltyItem(
      data: { list: { connect: { id: $listId } }, novelty: { connect: { id: $noveltyId } } }
    ) {
      id
    }
  }
`)

export const REMOVE_NOVELTY_FROM_CUSTOM_LIST = gql(/* GraphQL */ `
  mutation RemoveNoveltyFromCustomList($id: ID!) {
    deleteUserCustomListNoveltyItem(where: { id: $id }) {
      id
    }
  }
`)
